<template>
    <LoaderOverlay v-if="loading && isInitialLoad" />
  <!-- Affichage de l'état vide si pas admin -->
    <EmptyState
      v-else-if="!loading && !hasAdminRole"
      :isUnauthorized="true"
    />
    <template v-else>
      <div class="page-header">
        <h2>Tableau de bord</h2>
        <p class="date">{{ formattedDate }}</p>
      </div>
      <div class="stats-section">
        <h3>Activité ce Jour</h3>
        <div class="stats-grid">
          <!-- Trajets aujourd'hui -->
          <div class="card card-admin">
            <div class="chart-wrapper">
              <LineChart :key="refreshKey" :data="chartDataToday" />
            </div>
            <div>
              <div class="stat-label">Trajets</div>
              <div class="stat-value">{{ stats.tripsToday }}</div>
              <div class="stat-meta">{{ getTodayStats(stats.tripsToday) }}</div>
            </div>
          </div>
          <!-- Tickets vendus aujourd'hui -->
          <div class="card card-admin">
            <div class="chart-wrapper">
              <LineChart :key="refreshKey" :data="chartDataTicketsToday" />
            </div>
            <div>
              <div class="stat-label">Tickets vendus</div>
              <div class="stat-value">{{ stats.ticketsToday }}</div>
              <div class="stat-meta">{{ getTodayStats(stats.ticketsToday) }}</div>
            </div>
          </div>
          <!-- Voyageurs prévus aujourd'hui (AU MILIEU + couleur eclat) -->
          <div class="card card-admin">
            <div class="chart-wrapper">
              <LineChart :key="refreshKey" :data="chartDataPassengersToday" />
            </div>
            <div>
              <div class="stat-label">Voyageurs</div>
              <div class="stat-value">{{ stats.passengersToday }}</div>
              <div class="stat-meta">{{ getTodayStats(stats.passengersToday) }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="stats-section">
        <h3>Ce mois</h3>
        <div class="stats-grid">
          <div class="card card-admin">
            <div class="chart-wrapper chart-wrapper-bar">
              <BarChart :key="refreshKey" :data="chartDataMonth" />
            </div>
            <div>
              <div class="stat-label">Trajets</div>
              <div class="stat-value">{{ stats.tripsMonth }}</div>
              <div class="stat-meta">Moyenne: {{ getMonthAvg(stats.tripsMonth) }}/jour</div>
            </div>
          </div>
          <div class="card card-admin">
            <div class="chart-wrapper chart-wrapper-bar">
              <BarChart :key="refreshKey" :data="chartDataTicketsMonth" />
            </div>
            <div>
              <div class="stat-label">Tickets</div>
              <div class="stat-value">{{ stats.ticketsMonth }}</div>
              <div class="stat-meta">Moyenne: {{ getMonthAvg(stats.ticketsMonth) }}/jour</div>
            </div>
          </div>
        </div>
      </div>
      <div class="stats-section">
        <h3>Chiffres</h3>
        <div class="stats-grid">
          <div class="card card-admin card-stat">
            <div class="stat-label">CA aujourd'hui</div>
            <div class="stat-value">{{ cfa.format(stats.revenueToday) }}</div>
            <div class="stat-meta">Panier moyen: {{ cfa.format(stats.avgTicketToday) }}</div>
          </div>
          <div class="card card-admin card-stat">
            <div class="stat-label">CA ce mois</div>
            <div class="stat-value">{{ cfa.format(stats.revenueMonth) }}</div>
          </div>
          <div class="card card-admin card-stat">
            <div class="stat-label">Nouveaux utilisateurs aujourd'hui</div>
            <div class="stat-value">{{ stats.newUsersToday }}</div>
          </div>
          <div class="card card-admin card-stat">
            <div class="stat-label">Nouveaux utilisateurs ce mois</div>
            <div class="stat-value">{{ stats.newUsersMonth }}</div>
          </div>
        </div>
      </div>
      <div class="stats-section">
        <h3>Ressources</h3>
        <div class="stats-grid">
          <div class="card card-admin card-stat">
            <div class="stat-label">Utilisateurs</div>
            <div class="stat-value">{{ stats.users }}</div>
          </div>
          <div class="card card-admin card-stat">
            <div class="stat-label">Véhicules</div>
            <div class="stat-value">{{ stats.vehicles }}</div>
          </div>
          <div class="card card-admin card-stat">
            <div class="stat-label">Compagnies</div>
            <div class="stat-value">{{ stats.companies }}</div>
          </div>
          <div class="card card-admin card-stat">
            <div class="stat-label">Villes</div>
            <div class="stat-value">{{ stats.cities }}</div>
          </div>
          <div class="card card-admin stat">
            <div class="stat-label">Adresses</div>
            <div class="stat-value">{{ stats.addresses }}</div>
          </div>
        </div>
      </div>
    <!-- Affichage de l'info box -->
      <InfoBox v-if="error" type="error">
        {{ error }}
      </InfoBox>
    </template>
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, onMounted, computed } from 'vue'
import { useUserStore } from '@/stores/userStore'
import LoaderOverlay from '@/components/LoaderOverlay.vue'
import EmptyState from '@/components/EmptyState.vue'
import LineChart from '@/components/Charts/LineChart.vue'
import BarChart from '@/components/Charts/BarChart.vue'
import InfoBox from '@/components/InfoBox.vue'

const userStore = useUserStore()
const refreshKey = ref(0)
const isInitialLoad = ref(true)
const eur = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
const cfa = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' })

const stats = ref({
  tripsToday: 0,
  tripsMonth: 0,
  ticketsToday: 0,
  ticketsMonth: 0,
  users: 0,
  vehicles: 0,
  companies: 0,
  cities: 0,
  addresses: 0,
  passengersToday: 0,
  revenueToday: 0,
  revenueMonth: 0,
  avgTicketToday: 0,
  newUsersToday: 0,
  newUsersMonth: 0
})

const loading = ref(false)
const error = ref('')

const chartDataToday = ref({
  labels: Array.from({length: 24}, (_, i) => `${i}h`),
  datasets: [{
    label: 'Trajets',
    data: Array(24).fill(0),
    borderColor: '#67944C',
    backgroundColor: 'rgba(103, 148, 76, 0.1)',
    tension: 0.4,
    fill: true,
    pointRadius: 0,
    borderWidth: 2
  }]
})

const chartDataTicketsToday = ref({
  labels: Array.from({length: 24}, (_, i) => `${i}h`),
  datasets: [{
    label: 'Tickets',
    data: Array(24).fill(0),
    borderColor: '#B84446',
    backgroundColor: 'rgba(184, 68, 70, 0.1)',
    tension: 0.4,
    fill: true,
    pointRadius: 0,
    borderWidth: 2
  }]
})

const chartDataMonth = ref({
  labels: [],
  datasets: [{
    label: 'Trajets',
    data: [],
    backgroundColor: 'rgba(103, 148, 76, 0.7)',
    borderRadius: 4,
    borderSkipped: false
  }]
})

const chartDataTicketsMonth = ref({
  labels: [],
  datasets: [{
    label: 'Tickets',
    data: [],
    backgroundColor: 'rgba(184, 68, 70, 0.7)',
    borderRadius: 4,
    borderSkipped: false
  }]
})

const chartDataPassengersToday = ref({
  labels: Array.from({ length: 24 }, (_, i) => `${i}h`),
  datasets: [{
    label: 'Voyageurs',
    data: Array(24).fill(0),
    borderColor: '#F9CB5C',
    backgroundColor: 'rgba(239, 227, 197, 0.1)',
    tension: 0.4,
    fill: true,
    pointRadius: 0,
    borderWidth: 2
  }]
})

const hasAdminRole = computed(() => {
  return userStore.user?.roles?.includes('ROLE_ADMIN') || false
})

const formattedDate = computed(() => {
  const today = new Date()
  return today.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
})

const today = new Date()
const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)

const isToday = (dateString) => {
  const date = new Date(dateString)
  return date.toDateString() === today.toDateString()
}

const isThisMonth = (dateString) => {
  const date = new Date(dateString)
  return date >= startOfMonth && date <= endOfMonth
}

const getTodayStats = (count) => {
  if (count === 0) return 'Pas encore d\'activité'
  if (count === 1) return '1 enregistrement'
  return `${count} enregistrements`
}

const getMonthAvg = (count) => {
  const daysInMonth = endOfMonth.getDate()
  const avg = (count / daysInMonth).toFixed(1)
  return avg
}

const generateHourlyData = (items, dateKey) => {
  const hourly = new Array(24).fill(0)
  items.forEach(item => {
    if (item[dateKey] && isToday(item[dateKey])) {
      const hour = new Date(item[dateKey]).getHours()
      hourly[hour]++
    }
  })
  return hourly
}

const generateDailyData = (items, dateKey) => {
  const daysInMonth = endOfMonth.getDate()
  const daily = new Array(daysInMonth).fill(0)
  items.forEach(item => {
    if (item[dateKey] && isThisMonth(item[dateKey])) {
      const day = new Date(item[dateKey]).getDate() - 1
      daily[day]++
    }
  })
  return daily
}

onMounted(async () => {
  if (hasAdminRole.value) {
    await loadData()
  }
  isInitialLoad.value = false
})

const loadData = async () => {
  loading.value = true
  error.value = ''

  try {
    const [usersRes, vehiclesRes, companiesRes, citiesRes, addressesRes, trips, tickets] = await Promise.all([
      fetch(config.apiUrl+ '/user/admin', { credentials: 'include' }),
      fetch(config.apiUrl + '/vehicles', { credentials: 'include' }),
      fetch(config.apiUrl + '/companies', { credentials: 'include' }),
      fetch(config.apiUrl + '/cities', { credentials: 'include' }),
      fetch(config.apiUrl + '/addresses', { credentials: 'include' }),
      fetchAllTrips(),
      fetchAllTickets()
    ])

    if (usersRes.ok) {
      const data = await usersRes.json()
      stats.value.users = data.users && Array.isArray(data.users) ? data.users.length : 0
      const users = (data.users && Array.isArray(data.users)) ? data.users : []
      stats.value.newUsersToday = users.filter(u => isToday(u.createdAt)).length
      stats.value.newUsersMonth = users.filter(u => isThisMonth(u.createdAt)).length
    }

    if (vehiclesRes.ok) {
      const data = await vehiclesRes.json()
      stats.value.vehicles = Array.isArray(data) ? data.length : 0
    }

    if (companiesRes.ok) {
      const data = await companiesRes.json()
      stats.value.companies = Array.isArray(data) ? data.length : 0
    }

    if (citiesRes.ok) {
      const data = await citiesRes.json()
      stats.value.cities = Array.isArray(data) ? data.length : 0
    }

    if (addressesRes.ok) {
      const data = await addressesRes.json()
      stats.value.addresses = Array.isArray(data) ? data.length : 0
    }

    if (trips && Array.isArray(trips) && trips.length > 0) {
      stats.value.tripsToday = trips.filter(t => isToday(t.timeToStart)).length
      stats.value.tripsMonth = trips.filter(t => isThisMonth(t.timeToStart)).length

      const hourlyTrips = generateHourlyData(trips, 'timeToStart')
      chartDataToday.value.datasets[0].data = hourlyTrips

      const dailyTrips = generateDailyData(trips, 'timeToStart')
      const daysInMonth = endOfMonth.getDate()
      chartDataMonth.value.labels = Array.from({length: daysInMonth}, (_, i) => i + 1)
      chartDataMonth.value.datasets[0].data = dailyTrips
    }

    const getTicketPrice = (t) => {
      const p = Number(t?.trip?.price ?? 0)
      return Number.isFinite(p) ? p : 0
    }

    if (tickets && Array.isArray(tickets) && tickets.length > 0) {
      const ticketsTodayArr = tickets.filter(t => isToday(t.createdAt))
      const ticketsMonthArr = tickets.filter(t => isThisMonth(t.createdAt))

      stats.value.ticketsToday = ticketsTodayArr.length
      stats.value.ticketsMonth = ticketsMonthArr.length

      // Calcul des voyageurs (somme des passengers par ticket)
      stats.value.passengersToday = ticketsTodayArr.reduce((sum, t) => {
        const passengers = Number(t?.passengers ?? 1) // ou t?.passengerCount selon ton API
        return sum + (Number.isFinite(passengers) ? passengers : 1)
      }, 0)

      // Chart voyageurs par heure
      const hourlyPassengers = Array(24).fill(0)
      let totalPassengersToday = 0

      tickets.forEach(ticket => {
        const tripStart = ticket?.trip?.timeToStart
        if (tripStart && isToday(tripStart)) {
          const hour = new Date(tripStart).getHours()
          const passengers = Number(ticket?.passengers ?? 1)
          const count = Number.isFinite(passengers) ? passengers : 1
          hourlyPassengers[hour] += count
          totalPassengersToday += count
        }
      })

      stats.value.passengersToday = totalPassengersToday
      chartDataPassengersToday.value.datasets[0].data = hourlyPassengers
      // const hourlyPassengers = Array(24).fill(0)
      // ticketsTodayArr.forEach(ticket => {
      //   const hour = new Date(ticket.createdAt).getHours()
      //   const passengers = Number(ticket?.passengers ?? 1)
      //   hourlyPassengers[hour] += (Number.isFinite(passengers) ? passengers : 1)
      // })
      // chartDataPassengersToday.value.datasets[0].data = hourlyPassengers

      console.log('ticket sample', tickets[0])
      console.log('price sample', tickets[0]?.price)

      // € gagnés
      stats.value.revenueToday = ticketsTodayArr.reduce((sum, t) => sum + getTicketPrice(t), 0)
      stats.value.revenueMonth = ticketsMonthArr.reduce((sum, t) => sum + getTicketPrice(t), 0)

      // Charts (inchangés)
      chartDataTicketsToday.value.datasets[0].data = generateHourlyData(tickets, 'createdAt')

      const dailyTickets = generateDailyData(tickets, 'createdAt')
      const daysInMonth = endOfMonth.getDate()
      chartDataTicketsMonth.value.labels = Array.from({ length: daysInMonth }, (_, i) => i + 1)
      chartDataTicketsMonth.value.datasets[0].data = dailyTickets
    }

    refreshKey.value++

  } catch (err) {
    error.value = 'Erreur lors du chargement des données'
    console.error(err)
  } finally {
    loading.value = false
  }
}

const fetchAllTrips = async () => {
  try {
    const res = await fetch(config.apiUrl + '/trips', {
      credentials: 'include'
    })
    if (res.ok) {
      return await res.json()
    }
    return []
  } catch (err) {
    console.error('Error fetching trips:', err)
    return []
  }
}

const fetchAllTickets = async () => {
  try {
    const res = await fetch(config.apiUrl + '/tickets', {
      credentials: 'include'
    })
    if (res.ok) {
      return await res.json()
    }
    return []
  } catch (err) {
    error.value = 'Erreur lors du chargement des tickets'
    console.error('Error fetching tickets:', err)
    return []
  }
}
</script>

<style scoped>
.stats-section h3 {
  margin: 0 0 1rem 0;
  font-size: 0.95rem;
  color: var(--admin-label-color);
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}
.stat-label {
  font-size: 0.8rem;
  color: var(--admin-muted);
  margin-bottom: 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--jungle);
  margin-bottom: 0.25rem;
}
.stat-meta {
  font-size: 0.75rem;
  color: var(--admin-stat-meta);
}

.chart-wrapper {
  flex: 1;
  position: relative;
  margin-bottom: 1rem;
  min-height: 100px;
  width: 100%;
}
.chart-wrapper:deep(canvas) {
  max-height: 100px !important;
}
.chart-wrapper.chart-wrapper-bar {
  min-height: 250px;
}
.chart-wrapper.chart-wrapper-bar:deep(canvas) {
  max-height: 280px !important;
}
@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-card.stat-card-lg {
    min-height: 150px;
  }
}
</style>
